import { type BankAccount, JOURNAL_LINE_ACCOUNT_TYPE } from "@/apis/gql/types";
import { BankAccountJournalAccountPicker } from "@/components/shared/form/bank-account-journal-account-picker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useLedgerFeatureFlag from "@/hooks/use-ledger-feature-flag";
import { AccountEntityType } from "@/lib/constants/account-types";
import getAccountTypeIcon from "@/lib/miscellaneous/get-account-type-icon";
import { cn, formatIban } from "@/lib/utils";
import type { TranslationKey } from "@/localisation/types";
import { useDeleteBankAccount } from "@/queries/bank-accounts/delete";
import { useUpdateJournalAccounts } from "@/queries/ledger/accounts/update";
import { AlertCircle, Check, Circle, Loader2, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type AccountCardProps = {
  account: Pick<
    BankAccount,
    | "bankAccountId"
    | "name"
    | "type"
    | "iban"
    | "defaultAccount"
    | "accountNumber"
    | "journalAccounts"
  >;
  onSetDefaultAccount?: (
    accountId: string,
    isCurrentlyDefault: boolean,
  ) => void;
  onSetJournalAccount?: (journalAccountId: string) => Promise<void>;
  onDelete?: () => void;
  isDefaultAccount?: boolean;
};

export default function AccountCard({
  account,
  onSetDefaultAccount,
  onSetJournalAccount,
  onDelete,
  isDefaultAccount = false,
}: AccountCardProps) {
  const { isLedgerEnabled } = useLedgerFeatureFlag();

  const { t } = useTranslation();
  const [deleteBankAccount, { loading: deleting }] = useDeleteBankAccount();

  const AccountIcon = getAccountTypeIcon(account.type);

  const hasJournalAccounts = useMemo(() => {
    if (!isLedgerEnabled) {
      return true;
    }

    return account.journalAccounts ? account.journalAccounts.length > 0 : false;
  }, [account.journalAccounts]);

  const journalAccount = account.journalAccounts?.[0] ?? undefined;

  const handleDelete = async () => {
    await deleteBankAccount({ bankAccountId: account.bankAccountId }, () => {
      onDelete?.();
    });
  };

  return (
    <div
      className={cn(
        "rounded-md border transition-colors duration-200 overflow-hidden",
        !hasJournalAccounts
          ? "bg-amber-50/50 border-amber-200"
          : isDefaultAccount
            ? "bg-gray-100 border-gray-200"
            : "bg-gray-50 border-gray-100",
      )}
    >
      <div
        tabIndex={0}
        role={onSetDefaultAccount ? "button" : undefined}
        onClick={
          onSetDefaultAccount
            ? () => onSetDefaultAccount(account.bankAccountId, isDefaultAccount)
            : undefined
        }
        className={cn(
          "group flex items-center justify-between p-3 transition-colors duration-200 focus:outline-none",
          onSetDefaultAccount && "hover:cursor-pointer hover:bg-gray-100",
        )}
      >
        <div className="flex items-center space-x-3">
          <div
            className={cn(
              "h-8 w-8 rounded-full flex items-center justify-center",
              !hasJournalAccounts
                ? "bg-amber-100 text-amber-700"
                : "bg-gray-200 text-gray-600",
              isDefaultAccount && "border border-gray-200",
            )}
          >
            <AccountIcon className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h5 className="text-sm font-medium text-gray-900">
                {account.name}
              </h5>
            </div>
            <div className="flex flex-col space-y-0.5">
              <p className="text-xs text-gray-500 font-mono">
                {formatIban(account.iban)}
              </p>
              <p className="text-xs text-gray-500">
                {account.accountNumber !== "" && (
                  <>
                    <span className="font-medium">
                      {t("bank_connections.account_number")}:
                    </span>{" "}
                    {account.accountNumber} •{" "}
                  </>
                )}
                <span className="font-medium">
                  {t("bank_connections.type")}:
                </span>{" "}
                {
                  t(
                    `bank_connections.account_type.${account.type.toLowerCase()}` as TranslationKey,
                  ) as string
                }
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {isDefaultAccount && (
            <Badge
              variant="secondary"
              className="bg-gray-100 text-gray-700 border-gray-200"
            >
              {t("bank_connections.default_account")}
            </Badge>
          )}
          {onSetDefaultAccount && (
            <div
              className={cn(
                "h-9 w-9 items-center flex justify-center rounded-md transition-colors cursor-pointer",
                isDefaultAccount
                  ? "text-gray-900 hover:text-gray-800 hover:bg-gray-100"
                  : "text-gray-400 hover:text-gray-900 hover:bg-gray-200",
              )}
            >
              {isDefaultAccount ? (
                <div
                  className="relative transition-transform group-hover:scale-110"
                  style={{ width: "19px", height: "19px" }}
                >
                  <div className="absolute inset-0 rounded-full bg-gray-900"></div>
                  <Check
                    className="absolute inset-0 text-white p-1"
                    style={{ width: "19px", height: "19px" }}
                  />
                </div>
              ) : (
                <Circle
                  className="transition-transform group-hover:scale-110"
                  style={{ width: "19px", height: "19px" }}
                />
              )}
            </div>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete();
                }}
                disabled={deleting}
              >
                {deleting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <X className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Konto entfernen</TooltipContent>
          </Tooltip>
        </div>
      </div>

      {isLedgerEnabled && (
        <div
          className={cn(
            "border-t px-3 py-2",
            !hasJournalAccounts
              ? "border-amber-200 bg-amber-50"
              : "border-gray-200 bg-gray-50",
          )}
        >
          {!hasJournalAccounts && (
            <div className="flex items-center gap-1 text-amber-700 mb-2">
              <AlertCircle className="h-3.5 w-3.5 self-start mt-0.5" />
              <div className="flex flex-col">
                <span className="text-xs font-medium">
                  {t("bank_connections.no_journal_account")}
                </span>
                <span className="text-xs text-gray-500">
                  {t("bank_connections.no_journal_account_description")}
                </span>
              </div>
            </div>
          )}
          {hasJournalAccounts && (
            <div className="flex flex-col mb-2">
              <span className="text-xs font-medium">
                {t("bank_connections.journal_account")}
              </span>
              <span className="text-xs text-gray-500">
                {t("bank_connections.journal_account_description")}
              </span>
            </div>
          )}
          <JournalAccountPicker
            journalAccountId={journalAccount?.journalAccountId ?? null}
            bankAccountId={account.bankAccountId}
            onSetJournalAccount={onSetJournalAccount}
          />
        </div>
      )}
    </div>
  );
}

const JournalAccountPicker = ({
  journalAccountId,
  bankAccountId,
  onSetJournalAccount,
}: {
  journalAccountId: string | null;
  bankAccountId: string;
  onSetJournalAccount?: (journalAccountId: string) => Promise<void>;
}) => {
  const [updateAccount] = useUpdateJournalAccounts();

  const onSubmit = async (journalAccountId: string) => {
    await updateAccount(
      {
        journalAccountIds: [journalAccountId],
        entityId: bankAccountId,
        entityType: AccountEntityType.BANK_ACCOUNT,
        accountType: JOURNAL_LINE_ACCOUNT_TYPE.ASSET,
      },
      () => {
        onSetJournalAccount?.(journalAccountId);
      },
    );
  };

  return (
    <BankAccountJournalAccountPicker
      onSubmit={onSubmit}
      journalAccountId={journalAccountId}
      entityId={bankAccountId}
    />
  );
};
